/**
 * Common response definitions for v0 API routes
 *
 * This module provides reusable response definitions for error responses
 * and common headers to reduce duplication across route definitions.
 */

/**
 * Rate limit headers included in 429 responses
 */
export const rateLimitHeaders = {
  "X-RateLimit-Limit": {
    schema: {
      type: "integer" as const,
    },
    description:
      "The number of requests remaining in the current rate limit window.",
  },
  "X-RateLimit-Remaining": {
    schema: {
      type: "integer" as const,
    },
    description:
      "The number of requests remaining in the current rate limit window.",
  },
  "X-RateLimit-Reset": {
    schema: {
      type: "string" as const,
    },
    description: "The timestamp when the rate limit will be reset.",
  },
  "Retry-After": {
    schema: {
      type: "string" as const,
    },
    description: "The number of seconds to wait before retrying.",
  },
} as const;

/**
 * Retry-After header included in 503 responses
 */
export const retryAfterHeader = {
  "Retry-After": {
    schema: {
      type: "string" as const,
    },
    description: "The number of seconds to wait before retrying.",
  },
} as const;

/**
 * Common error response definitions for all v0 API routes
 *
 * These responses follow the JSON:API error response format and are
 * consistent across all endpoints.
 */
export const commonErrorResponses = {
  /**
   * 400 Bad Request - Invalid request parameters or body
   */
  400: {
    description: "Bad Request",
    content: {
      "application/json": {
        schema: {
          $ref: "#/components/schemas/ErrorsResponse",
        },
      },
    },
  },
  /**
   * 401 Unauthorized - Missing or invalid authentication
   */
  401: {
    description: "Unauthorized",
    content: {
      "application/json": {
        schema: {
          $ref: "#/components/schemas/ErrorsResponse",
        },
      },
    },
  },
  /**
   * 429 Too Many Requests - Rate limit exceeded
   * Includes rate limit headers
   */
  429: {
    description: "Too Many Requests",
    headers: rateLimitHeaders,
    content: {
      "application/json": {
        schema: {
          $ref: "#/components/schemas/ErrorsResponse",
        },
      },
    },
  },
  /**
   * 500 Server Error - Internal server error
   */
  500: {
    description: "Server Error",
    content: {
      "application/json": {
        schema: {
          $ref: "#/components/schemas/ErrorsResponse",
        },
      },
    },
  },
  /**
   * 503 Service Unavailable - Service temporarily unavailable
   * Includes Retry-After header
   */
  503: {
    description: "Service Unavailable",
    headers: retryAfterHeader,
    content: {
      "application/json": {
        schema: {
          $ref: "#/components/schemas/ErrorsResponse",
        },
      },
    },
  },
} as const;
